import React, { useState, useEffect } from 'react';
import { type MasterCategoryType, type MasterSearchFilters } from '../../../types/masterSearchTypes';
import { MASTER_REFERENCE_CONFIG } from '../../../data/masterReferenceConfig';

type Props = {
  currentCategory: MasterCategoryType;
  onCategoryChange: (cat: MasterCategoryType) => void;
  onSearch: (filters: MasterSearchFilters) => void;
};

export const MasterSearchForm = ({ currentCategory, onCategoryChange, onSearch }: Props) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    setCode('');
    setName('');
    setIncludeDeleted(false);
  }, [currentCategory]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({ code, name, includeDeleted });
  };

  const handleClear = () => {
    setCode('');
    setName('');
    setIncludeDeleted(false);
    onSearch({ code: '', name: '', includeDeleted: false });
  };

  // ★追加: スイッチ切り替え時に即座に検索を実行するハンドラ
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.checked;
    setIncludeDeleted(newVal);
    // State更新と同時に親の検索処理を呼ぶ
    onSearch({ code, name, includeDeleted: newVal });
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4">
        
        <div className="flex flex-col gap-1">
          <label className="text-sm font-bold text-gray-700">対象マスタ</label>
          <select 
            value={currentCategory}
            onChange={(e) => onCategoryChange(e.target.value as MasterCategoryType)}
            className="border border-blue-300 rounded px-3 py-2 w-64 bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(MASTER_REFERENCE_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-600">コード</label>
          <input 
            type="text" 
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="完全/前方一致"
          />
        </div>

        <div className="flex flex-col gap-1 flex-grow">
          <label className="text-sm text-gray-600">名称 (あいまい)</label>
          <div className="relative">
            <input 
              type="text" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`${MASTER_REFERENCE_CONFIG[currentCategory].label.replace('マスタ', '')}名を入力...`}
            />
          </div>
        </div>

        {/* スイッチ（トグル）UI */}
        <div className="flex flex-col gap-1 pb-2">
           <label className="relative inline-flex items-center cursor-pointer">
             <input 
               type="checkbox" 
               className="sr-only peer"
               checked={includeDeleted}
               onChange={handleSwitchChange} 
             />
             <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
             <span className="ml-3 text-sm font-medium text-gray-700 select-none">削除済を含める</span>
           </label>
        </div>

        <div className="flex gap-2 pb-0.5">
          <button 
            type="submit" 
            className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 transition-colors font-medium shadow-sm"
          >
            検索
          </button>
          <button 
            type="button" 
            onClick={handleClear}
            className="text-gray-600 px-4 py-2 hover:bg-gray-100 rounded transition-colors border border-gray-300 bg-white"
          >
            クリア
          </button>
        </div>
      </form>
    </div>
  );
};